import './lib/posthog'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './lib/konvaSetup'
import App from './App.tsx'
import { initTelemetry, addBreadcrumb } from './lib/telemetry'
import { initWebVitals } from "./lib/webVitals"
import { initTelemetryAuthBridge } from './lib/telemetryAuthBridge'
import { initSessionLog } from './lib/sessionLog'
import { initAttribution } from './lib/attribution'
import { maybeSeedDemoRoom } from './lib/demoRooms'

// Telemetry first — must be initialized before any other module
// starts emitting events or before the first render so the
// ErrorBoundary in App can route a boot-time crash to Cloud Error
// Reporting. No-ops when VITE_GCP_ERROR_REPORTING_KEY /
// VITE_FIREBASE_MEASUREMENT_ID aren't set, so contributor builds
// without prod keys are unaffected.
initTelemetry()
initTelemetryAuthBridge()
initSessionLog()
// Core Web Vitals for the first page, reported once on hide.
initWebVitals()

// Capture marketing-attribution UTMs (utm_source, fbclid, …) into
// first-/last-touch storage and fire the one-shot `campaign_landed`
// event. Must run AFTER `initTelemetry()` so the analytics + Meta
// Pixel forwarders are ready; the function is itself a no-op when the
// URL carries no UTM params, so cost on organic traffic is zero.
initAttribution()

// When a deploy ships, the new `index.html` references new content-hashed
// chunk filenames. Already-loaded tabs still hold references to the old
// filenames, so dynamic imports (e.g. `await import("firebase/functions")`
// when the user opens Share) try to fetch chunks that 404. Vite raises
// `vite:preloadError` for exactly this case — reload once per tab session
// to pick up the new chunk graph. The session-storage guard prevents an
// infinite reload loop if the new HTML is also broken.
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (event) => {
    const RELOAD_KEY = 'rc:preload-error-reloaded'
    addBreadcrumb('vite:preloadError', {
      already_reloaded: Boolean(sessionStorage.getItem(RELOAD_KEY)),
    }, 'warning')
    if (sessionStorage.getItem(RELOAD_KEY)) return
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
    event.preventDefault()
    window.location.reload()
  })
}

// If the URL carries `?demo=<id>` AND the canvas is empty, replace the
// active design with a seeded sample room so cold ad traffic sees a
// fully-furnished space on first paint instead of a blank canvas.
// Runs SYNCHRONOUSLY (not via dynamic import) so the seeded design is
// in the store before React's first render — async would flash an
// empty canvas for one frame. Guarded against clobbering an
// in-progress design by `isUnstartedRoom` inside; safe no-op when the
// URL doesn't carry the param.
maybeSeedDemoRoom()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

// Kick off the cloud sync orchestrators AFTER the first render is scheduled.
// They are idempotent, no-op gracefully when Firebase isn't configured, and
// only matter for signed-in users whose library/products/shared rooms mirror
// to Firestore — none of which is needed for the first paint or the
// landing-room seed. Loading them lazily keeps the Firestore SDK and this
// setup work off the boot critical path. `requestIdleCallback` yields to the
// first frame first; `setTimeout` is the Safari fallback.
function initCloudSync() {
  void import('./lib/librarySync').then((m) => m.initLibrarySync())
  void import('./lib/productsSync').then((m) => m.initProductsSync())
  void import('./lib/customFurnitureSync').then((m) => m.initCustomFurnitureSync())
  void import('./lib/sharedRoomsRegistry').then((m) => m.initSharedRoomsRegistry())
}

if (typeof window !== 'undefined') {
  const ric = (window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
  }).requestIdleCallback
  if (typeof ric === 'function') ric(initCloudSync, { timeout: 2000 })
  else setTimeout(initCloudSync, 0)
} else {
  initCloudSync()
}
